/**
 * DoctorAddReviewModal Component
 *
 * Description: A modal component for adding a review for a specific appointment.
 * It includes functionality to rate a service and leave a comment,
 * and utilizes an API to submit the review.
 *
 * Props:
 * - isOpen (boolean): Whether the modal is open or not.
 * - onClose (function): Function to close the modal.
 * - onSuccess (function): Function to be called upon successful submission.
 * - data (object): Data of the doctor for whom the review is being written, including name, profile image, and specialization.
 * - appoinmentId (string): The ID of the appointment being reviewed.
 * - comment (string): The current comment entered by the user.
 * - star (number): The current star rating entered by the user.
 * - setComment (function): Function to update the comment value.
 * - setStar (function): Function to update the star rating.
 *
 * File Name: DoctorAddReviewModal.tsx
 * Date: 01-10-2024
 *
 * Marker: DOC_START
 */

import React, { FormEvent, useState } from "react";
import {
  faArrowLeft,
  faStar,
  faThumbsDown,
  faThumbsUp,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "@mui/material";
import PrimaryBtn from "../GlobalComponents/primaryBtn";
import defaultProfile from "src/assets/images/default_profile_image.png";
import SimpleInput from "../SimpleInput";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import bgmodal from "src/assets/images/banner-bg-white.png";
import { printServiceDuration } from "src/helpers/helperFunctions";
import CustomWebsiteDropdown from "../GlobalComponents/Widget/CustomWebsiteDropdown";
import GradientBtn from "../WebsiteComponents/GradientBtn";

// Style for the modal container
const customeStyle: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
};

// Style for the close button container
const backBtnMaain: React.CSSProperties = {
  position: "absolute",
  top: -10,
  right: -10,
  backgroundColor: "red",
  borderRadius: 100,
  width: 20,
  height: 20,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

interface IReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  data: {
    fullName: string;
    profileImage: string;
    specialization?: string[];
    serviceDuration?: string;
    healthCareProvider?: string;
  };
  appoinmentId: string;
  appointmentType: "doctorAppointment" | "homecareAppointment";
  productId?: any;
}

const DoctorAddReviewModal: React.FC<IReviewModalProps> = ({
  isOpen = false,
  onClose = () => {},
  data = {
    fullName: "",
    profileImage: "",
    specialization: [],
    serviceDuration: "",
    healthCareProvider: "",
  },
  appoinmentId = "",
  appointmentType,
}) => {
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedWaitLong, setSelectedWaitLong] = useState<{
    label: string;
    value: string;
  } | null>(null);

  const [selectedConsultationTime, setSelectedConsultationTime] = useState<{
    label: string;
    value: string;
  } | null>(null);

  const [selectedDiagnosis, setSelectedDiagnosis] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [comment, setComment] = useState<string>("");
  const [selectedRecommendation, setSelectedRecommendation] =
    useState<number>(0);

  // API_CALL: Submit a review for the selected doctor
  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!selectedWaitLong?.value || !selectedConsultationTime?.value || !selectedDiagnosis?.value || !selectedRecommendation || !comment) {
      toast("Please fill all the fields", { type: "error" });
      return;
    }

    let payload = {
      appointmentId: appoinmentId,
      longConsultation: selectedWaitLong?.value || "",
      doctorConsultation: selectedConsultationTime?.value || "",
      diagnosis: selectedDiagnosis?.value || "",
      like: String(selectedRecommendation || "0"),
      comment: comment || "",
    };

    setIsLoader(true);
    const { res, err } = await httpRequest<{ message: string }>({
      method: "post",
      path: "/review/person",
      params: payload,
    });
    if (res) {
      toast(res?.message || "Success", { type: "success" });
      onClose();
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  const handleClose = () => {
    setSelectedCategory("");
    onClose();
  };

  let WaitLongOptions = [
    {
      label: "Less Then 10 Mins",
      value: "less then 10 Mins",
    },
    {
      label: "10 Mins",
      value: "10 mins",
    },
    {
      label: "15 Mins",
      value: "15 mins",
    },
    {
      label: "20 Mins",
      value: "20 mins",
    },
    {
      label: "30 Mins",
      value: "30 mins",
    },
    {
      label: "45 Mins",
      value: "45 mins",
    },
    {
      label: "1 Hour",
      value: "1 hour",
    },
    {
      label: "2 Hour",
      value: "2 hour",
    },
    {
      label: "3 Hour",
      value: "3 hour",
    },
    {
      label: "4 Hour",
      value: "4 hour",
    },
    {
      label: "5 Hour",
      value: "5 hour",
    },
    {
      label: "6 Hour",
      value: "6 hour",
    },
  ];

  let ConsultationTimeOptions = [
    {
      label: "5 Mins",
      value: "5 mins",
    },
    {
      label: "10 Mins",
      value: "10 mins",
    },
    {
      label: "20 Mins",
      value: "20 mins",
    },
    {
      label: "30 Mins",
      value: "30 mins",
    },
    {
      label: "1 Hour",
      value: "1 hour",
    },
  ];

  let DiagnosisOptions = [
    {
      label: "Yes",
      value: "yes",
    },
    {
      label: "No",
      value: "no",
    },
  ];

  return (
    <Modal
      open={isOpen}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      onClose={handleClose}
    >
      {selectedCategory ? (
        <div
          style={customeStyle}
          className="flex items-center w-[35vw] flex-col side-bar-padding-3 xl:gap-3 lg:gap-2 gap-1.5 bg-white rounded-[8px]"
        >
          <button id="close-btn" onClick={handleClose} style={backBtnMaain}>
            <FontAwesomeIcon
              className="font-size-16px text-white"
              icon={faXmark}
              size="2x"
            />
          </button>
          <div className="flex gap-2 items-center relative justify-center w-full border-b border-[#E5E5E5] pb-2">
            <FontAwesomeIcon
              icon={faArrowLeft}
              className="text-[#2F2F31] absolute left-0 font-size-18px cursor-pointer"
              onClick={() => setSelectedCategory("")}
            />
            <h2 className="font-Poppins-Medium font-size-18px text-[#2F2F31]">
              Give Review
            </h2>
          </div>
          <div className="box-design flex flex-col side-bar-gap side-bar-padding w-full">
            <div className="flex gap-2 items-center pb-1.5 border-b border-[#E5E5E5]">
              <p className="font-Poppins-Medium font-size-18px text-[#202020]">
                Rate and Review You Experience
              </p>
            </div>
            <CustomWebsiteDropdown
              options={WaitLongOptions}
              setData={(data) => setSelectedWaitLong(data[0])}
              data={selectedWaitLong ? [selectedWaitLong] : []}
              labelText="Did you wait long for consultation?"
              newUI
              showRadioBtn
              placeholder="Select Wait Time"
              withoutMargin
            />
            <CustomWebsiteDropdown
              options={ConsultationTimeOptions}
              setData={(data) => setSelectedConsultationTime(data[0])}
              data={selectedConsultationTime ? [selectedConsultationTime] : []}
              labelText="Did doctor give you enough time for consultation?"
              newUI
              showRadioBtn
              placeholder="Select consultation time"
              withoutMargin
            />
            <CustomWebsiteDropdown
              options={DiagnosisOptions}
              setData={(data) => setSelectedDiagnosis(data[0])}
              data={selectedDiagnosis ? [selectedDiagnosis] : []}
              labelText="Are you satisfied with the diagnosis?"
              newUI
              showRadioBtn
              withoutMargin
            />
            <div className="w-full">
              <p className="font-Poppins-Medium mb-1 font-size-16px text-[2f2f31]">
                How likely are you to recommend this serve to your friend?
              </p>
              <div className="flex gap-2 items-center flex-wrap justify-between">
                {Array.from({ length: 10 }).map((_, index) => (
                  <div
                    onClick={() => setSelectedRecommendation(index + 1)}
                    key={index}
                    className={`flex w-[7%] items-center justify-center border border-[#707070] aspect-square rounded-full active:scale-95 transition-all duration-300 font-Poppins-Regular font-size-16px text-[#707070] cursor-pointer ${
                      selectedRecommendation >= index + 1
                        ? "bg-[#1FC23A] text-white border-none"
                        : ""
                    }`}
                  >
                    {index + 1}
                  </div>
                ))}
              </div>
              <div className="flex gap-2 items-center justify-between w-full mt-1">
                <p className={`font-Poppins-Regular font-size-15px text-[#303030] transition-all duration-1000 ${selectedRecommendation <= 3 ? "font-bold" : ""}`}>
                  Not Likely Yet
                </p>
                <p className={`font-Poppins-Regular font-size-15px text-[#303030] transition-all duration-1000 ${selectedRecommendation >= 7 ? "font-bold" : ""}`}>
                  Extremely Likely
                </p>
              </div>
            </div>
            <div className="w-full">
              <p className="font-Poppins-Medium mb-1 font-size-16px text-[2f2f31]">
                Are you satisfied with the diagnosis?
              </p>
              <textarea
                id="review-text-area"
                onChange={(e) => setComment(e.target.value)}
                value={comment}
                className="w-full h-[13vh] border border-[#ACACAC] xl:rounded-lg rounded-md p-2 font-Poppins-Regular font-size-16px text-[#707070] resize-none outline-none"
                placeholder="Help some other patients by sharing your experience"
                maxLength={200}
              />
              <p className="font-Poppins-Regular font-size-14px text-[#707070] text-right">
                {comment.length}/200
              </p>
            </div>
          </div>
          <GradientBtn
            ButtonText="Submit Review"
            btnExtraStyle={{
              width: "100%",
            }}
            onPress={handleSubmit}
            loader={isLoader}
          />
        </div>
      ) : (
        <div className="flex flex-col max-w-[450px] lg:w-[30vw] translate-y-[-50%] translate-x-[-50%] absolute top-1/2 left-1/2 2xl:gap-3 lg:gap-2 gap-1.5 items-center justify-center 2xl:p-7 lg:p-5 p-4 2xl:rounded-lg rounded-md shadow-all bg-banner-white-image">
          <h2 className="font-Poppins-Medium font-size-25px text-[#363636]">
            Submit Your Review
          </h2>
          <p className="font-Poppins-Regular font-size-16px text-[#686868]">
            Were you satisfied with your overall experience
          </p>
          <div className="flex gap-3 items-center justify-center 2xl:mt-4 lg:mt-3 mt-2">
            <div
              className="xl:w-[5vw] cursor-pointer active:scale-95 transition-all duration-300 w-[60px] aspect-square rounded-md bg-white shadow-all-light-2 flex flex-col gap-1 items-center justify-center"
              onClick={() => setSelectedCategory("yes")}
            >
              <FontAwesomeIcon
                icon={faThumbsUp}
                className="text-[#1FC23A] font-size-35px animate-bounce"
              />
              <p className="font-Poppins-Medium font-size-15px text-[#363636]">
                Yes
              </p>
            </div>
            <div
              className="xl:w-[5vw] w-[60px] cursor-pointer active:scale-95 transition-all duration-300 aspect-square rounded-md bg-white shadow-all-light-2 flex flex-col gap-1 items-center justify-center"
              onClick={onClose}
            >
              <FontAwesomeIcon
                icon={faThumbsDown}
                className="text-[#8E1212] font-size-35px"
              />
              <p className="font-Poppins-Medium font-size-15px text-[#363636]">
                No
              </p>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default DoctorAddReviewModal;
